import { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, ChevronRight, MousePointerClick, Pencil, PawPrint, Trash2, User, X } from "lucide-react";
import { CrudPage } from "../components/crud/CrudPage";
import { ModalBajaMascota } from "../components/mascotas/ModalBajaMascota";
import { useOpciones } from "../hooks/useOpciones";

interface Mascota {
  id: string;
  nombre: string;
  especie: string;
  raza?: string;
  fotoUrl?: string | null;
  cliente?: { nombre: string };
  solicitudEliminacion?: boolean;
  estadoReproductivo?: string | null;
  estadoReproductivoOtro?: string | null;
}

const especies = ["PERRO", "GATO", "AVE", "ROEDOR", "REPTIL", "EXOTICO", "OTRO"].map((e) => ({ value: e, label: e.charAt(0) + e.slice(1).toLowerCase() }));

// Color de acento por especie, solo para que la tarjeta se distinga de un
// vistazo en un listado grande — no tiene ningún otro significado.
const ACENTO_ESPECIE: Record<string, { franja: string; icono: string; fondo: string }> = {
  PERRO: { franja: "bg-blue-400", icono: "text-blue-600", fondo: "bg-blue-50" },
  GATO: { franja: "bg-violet-400", icono: "text-violet-600", fondo: "bg-violet-50" },
  AVE: { franja: "bg-amber-400", icono: "text-amber-600", fondo: "bg-amber-50" },
  ROEDOR: { franja: "bg-emerald-400", icono: "text-emerald-600", fondo: "bg-emerald-50" },
  REPTIL: { franja: "bg-teal-400", icono: "text-teal-600", fondo: "bg-teal-50" },
  EXOTICO: { franja: "bg-rose-400", icono: "text-rose-600", fondo: "bg-rose-50" },
  OTRO: { franja: "bg-slate-300", icono: "text-slate-500", fondo: "bg-slate-100" },
};
const sexos = [
  { value: "MACHO", label: "Macho" },
  { value: "HEMBRA", label: "Hembra" },
  { value: "DESCONOCIDO", label: "Desconocido" },
];
const estadosReproductivos = [
  { value: "FERTIL", label: "Fértil" },
  { value: "CASTRADO", label: "Castrado" },
  { value: "ESTERILIZADO", label: "Esterilizado" },
  { value: "OTRO", label: "Otro" },
];

export default function Mascotas() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { opciones: clientes } = useOpciones("/clientes", (c) => c.nombre);
  const recargarRef = useRef<(() => void) | null>(null);
  const [mascotaABajar, setMascotaABajar] = useState<Mascota | null>(null);

  // Si se llega desde "Ver mascotas" en la tarjeta de un cliente (apartado
  // Clientes), se filtra el listado a solo las mascotas de ese tutor.
  const clienteIdFiltro = searchParams.get("clienteId");
  const nombreClienteFiltro = clienteIdFiltro ? clientes.find((c) => c.value === clienteIdFiltro)?.label : undefined;

  return (
    <>
      {clienteIdFiltro ? (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-brand-100 bg-brand-50/60 p-4">
          <p className="text-sm text-slate-600">
            Mostrando solo las mascotas de{" "}
            <span className="font-semibold text-slate-700">{nombreClienteFiltro ?? "este tutor"}</span>.
          </p>
          <button
            className="btn-secondary !py-1.5 !text-xs"
            onClick={() => setSearchParams({}, { replace: true })}
          >
            <X size={13} /> Quitar filtro
          </button>
        </div>
      ) : (
        <div className="mb-4 flex items-start gap-3 rounded-2xl border border-brand-100 bg-brand-50/60 p-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-brand-600 shadow-sm">
            <MousePointerClick size={18} />
          </div>
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-slate-700">Da clic en cualquier mascota</span> para abrir su expediente
            completo: ahí puedes registrar consultas, recetas, vacunas, documentos y más.
          </p>
        </div>
      )}

      <CrudPage<Mascota>
        resource="/mascotas"
          seccion="mascotas"
        titulo="Mascotas"
        icono={<PawPrint size={20} />}
        descripcion="Pacientes registrados y su expediente clínico."
        nombreSingular="mascota"
        onRowClick={(row) => navigate(`/mascotas/${row.id}`)}
        onEliminarPersonalizado={(row) => setMascotaABajar(row)}
        recargarRef={recargarRef}
        filtrosFijos={clienteIdFiltro ? { clienteId: clienteIdFiltro } : undefined}
        vista="tarjetas"
        renderTarjeta={(r, { puedeEditar, puedeEliminar, onEditar, onEliminar }) => {
          const acento = ACENTO_ESPECIE[r.especie] ?? ACENTO_ESPECIE.OTRO;
          const especieLabel = especies.find((e) => e.value === r.especie)?.label ?? r.especie;
          const estadoLabel =
            r.estadoReproductivo === "OTRO"
              ? r.estadoReproductivoOtro || "Otro"
              : estadosReproductivos.find((e) => e.value === r.estadoReproductivo)?.label;
          return (
            <div className="card flex h-full flex-col overflow-hidden !p-0 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:border-brand-200">
              {/* Botones de editar/eliminar sobre la franja de color, arriba a la derecha */}
              <div className={`relative flex h-24 items-center justify-center ${acento.fondo}`}>
                <div className={`absolute inset-x-0 top-0 h-1.5 ${acento.franja}`} />
                {r.fotoUrl ? (
                  <img src={r.fotoUrl} alt={r.nombre} className="h-20 w-20 rounded-full border-4 border-white object-cover shadow-sm" />
                ) : (
                  <div className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-white bg-white shadow-sm">
                    <PawPrint size={28} className={acento.icono} />
                  </div>
                )}
                {(puedeEditar || puedeEliminar) && (
                  <div className="absolute right-2 top-3 flex gap-1" onClick={(e) => e.stopPropagation()}>
                    {puedeEditar && (
                      <button
                        className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-slate-500 shadow-sm hover:bg-white hover:text-slate-700"
                        title="Editar"
                        onClick={onEditar}
                      >
                        <Pencil size={13} />
                      </button>
                    )}
                    {puedeEliminar && (
                      <button
                        className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-red-400 shadow-sm hover:bg-white hover:text-red-600"
                        title="Dar de baja"
                        onClick={onEliminar}
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="flex flex-1 flex-col gap-2 p-4">
                <div>
                  <p className="flex items-center justify-center gap-1.5 text-center font-semibold text-slate-800">
                    {r.nombre}
                    {r.solicitudEliminacion && (
                      <span title="El cliente solicitó eliminar este expediente">
                        <AlertTriangle size={13} className="text-amber-500" />
                      </span>
                    )}
                  </p>
                  <p className="text-center text-xs text-slate-400">
                    {especieLabel}
                    {r.raza ? ` · ${r.raza}` : ""}
                  </p>
                </div>

                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <User size={13} className="shrink-0 text-slate-300" />
                  <span className="truncate">{r.cliente?.nombre ?? "Sin tutor asignado"}</span>
                </div>

                {estadoLabel && (
                  <span className={`badge w-fit ${acento.fondo} ${acento.icono}`}>{estadoLabel}</span>
                )}

                <div className="mt-auto flex items-center justify-center gap-1 border-t border-slate-100 pt-3 text-xs font-medium text-brand-600">
                  Ver expediente
                  <ChevronRight size={13} className="transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>
            </div>
          );
        }}
        campos={[
          { name: "fotoUrl", label: "Foto", type: "image", colSpan2: true },
          { name: "clienteId", label: "Tutor", type: "select", options: clientes, required: true },
          { name: "nombre", label: "Nombre de la mascota", required: true },
          { name: "especie", label: "Especie", type: "select", options: especies, default: "PERRO" },
          { name: "raza", label: "Raza" },
          { name: "sexo", label: "Sexo", type: "select", options: sexos },
          { name: "estadoReproductivo", label: "Estado reproductivo", type: "select", options: estadosReproductivos },
          {
            name: "estadoReproductivoOtro",
            label: "Especifica el estado reproductivo",
            type: "custom",
            colSpan2: true,
            render: (valores, onChange) =>
              valores.estadoReproductivo === "OTRO" ? (
                <div>
                  <label className="label">Especifica el estado reproductivo</label>
                  <input
                    className="input"
                    value={valores.estadoReproductivoOtro ?? ""}
                    onChange={(e) => onChange("estadoReproductivoOtro", e.target.value)}
                    placeholder="Ej. en tratamiento hormonal"
                  />
                </div>
              ) : null,
          },
          { name: "fechaNacimiento", label: "Fecha de nacimiento", type: "date" },
          { name: "peso", label: "Peso (kg)", type: "number" },
          { name: "color", label: "Color" },
          { name: "microchip", label: "Microchip" },
          { name: "notas", label: "Notas", type: "textarea", colSpan2: true },
        ]}
        columnas={[
          {
            key: "fotoUrl",
            label: "",
            render: (r) => (
              <div className="h-9 w-9 overflow-hidden rounded-full bg-slate-100">
                {r.fotoUrl && <img src={r.fotoUrl} alt={r.nombre} className="h-full w-full object-cover" />}
              </div>
            ),
          },
          {
            key: "nombre",
            label: "Nombre",
            render: (r) => (
              <span className="inline-flex items-center gap-1.5">
                {r.nombre}
                {r.solicitudEliminacion && (
                  <span title="El cliente solicitó eliminar este expediente">
                    <AlertTriangle size={13} className="text-amber-500" />
                  </span>
                )}
              </span>
            ),
          },
          { key: "especie", label: "Especie" },
          { key: "raza", label: "Raza" },
          {
            key: "estadoReproductivo",
            label: "Estado reproductivo",
            render: (r) =>
              r.estadoReproductivo === "OTRO"
                ? r.estadoReproductivoOtro || "Otro"
                : estadosReproductivos.find((e) => e.value === r.estadoReproductivo)?.label ?? "—",
          },
          { key: "cliente", label: "Tutor", render: (r) => r.cliente?.nombre ?? "—" },
        ]}
      />

      <ModalBajaMascota
        mascota={mascotaABajar}
        onClose={() => setMascotaABajar(null)}
        onBajaExitosa={() => {
          setMascotaABajar(null);
          recargarRef.current?.();
        }}
      />
    </>
  );
}
